import { OpenRouterConfig } from '../components/OpenRouterConfig';

// Tipos de arquivos suportados
export type SupportedFileType = 'image' | 'document' | 'audio' | 'video';

// Extensões de arquivo suportadas
export const SUPPORTED_EXTENSIONS = {
  image: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg'],
  document: ['.pdf', '.txt', '.md', '.docx', '.doc', '.csv', '.xlsx', '.xls'],
  audio: ['.mp3', '.wav', '.m4a', '.ogg', '.flac'],
  video: ['.mp4', '.mov', '.avi', '.mkv', '.webm']
} as const;

// Interface para arquivo carregado
export interface UploadedFile {
  id: string;
  file: File;
  type: SupportedFileType;
  name: string;
  size: number;
  url?: string; // Para preview
  base64?: string; // Para envio à API
  processed?: boolean;
  metadata?: {
    width?: number;
    height?: number;
    duration?: number;
    pages?: number;
    extractedText?: string;
  };
}

// Interface para configuração de múltiplos modelos
export interface MultiModelConfig {
  id: string;
  name: string;
  config: OpenRouterConfig;
  enabled: boolean;
  order: number;
  capabilities: ModelCapability[];
}

// Capacidades dos modelos
export type ModelCapability = 'text' | 'image' | 'audio' | 'video' | 'code' | 'reasoning' | 'creative';

// Interface para resposta multimodal
export interface MultimodalResponse {
  id: string;
  modelId: string;
  modelName: string;
  content: string;
  tokens: {
    input: number;
    output: number;
    total: number;
  };
  processingTime: number;
  timestamp: Date;
  files?: UploadedFile[];
  error?: string;
}

// Interface para sessão multimodal
export interface MultimodalSession {
  id: string;
  prompt: string;
  files: UploadedFile[];
  models: MultiModelConfig[];
  responses: MultimodalResponse[];
  createdAt: Date;
  updatedAt: Date;
}

// Interface para configuração do sistema multimodal
export interface MultimodalSystemConfig {
  maxFileSize: number; // em bytes
  maxFiles: number;
  enabledFileTypes: SupportedFileType[];
  defaultModels: string[];
  parallelProcessing: boolean;
  autoOptimizeImages: boolean;
}

// Interface para resultado do processamento
export interface ProcessingResult {
  success: boolean;
  file: UploadedFile;
  error?: string;
  extractedData?: {
    text?: string;
    metadata?: Record<string, any>;
  };
}

// Interface para status do processamento
export interface ProcessingStatus {
  fileId: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  progress: number;
  message?: string;
}

// Interface para comparação de respostas
export interface ResponseComparison {
  responses: MultimodalResponse[];
  winner?: string; // ID do modelo com melhor resposta
  metrics: {
    quality: number;
    relevance: number;
    completeness: number;
  };
}

// Configurações padrão
export const DEFAULT_MULTIMODAL_CONFIG: MultimodalSystemConfig = {
  maxFileSize: 10 * 1024 * 1024, // 10MB
  maxFiles: 5,
  enabledFileTypes: ['image', 'document'],
  defaultModels: [
    'anthropic/claude-3.5-sonnet',
    'openai/gpt-4-vision-preview', 
    'google/gemini-pro-vision'
  ],
  parallelProcessing: true,
  autoOptimizeImages: true
};

// Modelos recomendados por tipo de tarefa
export const RECOMMENDED_MODELS = {
  vision: ['anthropic/claude-3.5-sonnet', 'openai/gpt-4-vision-preview', 'google/gemini-1.5-pro'],
  text: ['anthropic/claude-3.5-sonnet', 'openai/gpt-4', 'google/gemini-1.5-pro'],
  code: ['anthropic/claude-3.5-sonnet', 'openai/gpt-4', 'deepseek/deepseek-coder'],
  creative: ['anthropic/claude-3.5-sonnet', 'openai/gpt-4', 'google/gemini-1.5-pro'],
  reasoning: ['anthropic/claude-3.5-sonnet', 'openai/o1-preview', 'google/gemini-1.5-pro']
} as const;